/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { animated, useSpring, useTrail } from '@react-spring/web';
import {
  Box,
  Grid,
  Button,
  AnimationState,
  useAnimationStateSelector,
  usePageTransitionActions,
  usePageTransitionDispatch,
  useThemeUI,
  useBreakpointIndex,
} from '@tishman/components';
import * as Icons from '@tishman/icons';

import { SearchForm } from '../SearchForm';

import type { SearchFormProps } from '../SearchForm';

export interface SearchMenuProps extends SearchFormProps {
  /**
   * Optional callback to call when the search menu close button is pressed.
   */
  onClose?: () => void;
}

export function SearchMenu({
  to,
  suggestions,
  onClose,
}: SearchMenuProps): React.JSX.Element {
  const actions = usePageTransitionActions();
  const dispatch = usePageTransitionDispatch();
  const themeUI = useThemeUI();
  const isMobile = useBreakpointIndex() < 2;

  const ctrlIds = useRef(new Set<number>());

  const [bgTransition, bgRef] = useSpring(() => ({
    config: { tension: 260, friction: 28, clamp: true },
    delay: 100,
    scaleY: 0,
  }));

  useEffect(() => {
    bgRef.current.forEach((ctrl) => {
      ctrlIds.current.add(ctrl.id);
    });
  }, [bgRef]);

  const [opacityTransitions, opacityRef] = useTrail(2, (_, ctrl) => {
    ctrlIds.current.add(ctrl.id);

    return {
      config: { tension: 720, friction: 30, clamp: true },
      opacity: 0,
    };
  });

  const animationState = useAnimationStateSelector([...ctrlIds.current]);

  useEffect(() => {
    const ctrlIds_ = ctrlIds.current;

    ctrlIds_.forEach((animationId) => {
      dispatch(
        actions.add({
          animationId,
          animationState: AnimationState.SHOULD_ENTER,
        }),
      );
    });

    return () => {
      ctrlIds_.forEach((animationId) => {
        dispatch(actions.remove(animationId));
      });
    };
  }, [actions, ctrlIds, dispatch]);

  useLayoutEffect(() => {
    switch (animationState) {
      case AnimationState.SHOULD_ENTER: {
        bgRef.start({
          config: { tension: 190, friction: 32 },
          onStart: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.IS_ENTERING,
              }),
            );
          },
          onRest: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.DID_ENTER,
              }),
            );
          },
          scaleY: 1,
        });

        opacityRef.start({
          config: { tension: 360, friction: 30 },
          delay: 100,
          onStart: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.IS_ENTERING,
              }),
            );
          },
          onRest: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.DID_ENTER,
              }),
            );
          },
          opacity: 1,
        });

        break;
      }
      case AnimationState.SHOULD_LEAVE: {
        bgRef.start({
          config: { tension: 260, friction: 28, clamp: true },
          delay: 100,
          onStart: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.IS_LEAVING,
              }),
            );
          },
          onRest: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.DID_LEAVE,
              }),
            );
          },
          scaleY: 0,
        });

        opacityRef.start({
          config: { tension: 720, friction: 30, clamp: true },
          onStart: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.IS_LEAVING,
              }),
            );
          },
          onRest: (_, ctrl) => {
            dispatch(
              actions.set({
                animationId: ctrl.id,
                animationState: AnimationState.DID_LEAVE,
              }),
            );
          },
          opacity: 0,
        });

        break;
      }
    }
  }, [actions, animationState, bgRef, dispatch, opacityRef]);

  return (
    <Box sx={{ position: 'relative', placeSelf: 'start stretch' }}>
      <animated.div
        style={{
          ...bgTransition,
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: themeUI.theme.colors.background,
          transformOrigin: 'top center',
        }}
      />
      <Grid
        gap={0}
        m="0 auto"
        sx={{
          width: '100%',
          height: '100%',
          minWidth: '320px',
          maxWidth: '1440px',
          position: 'relative',
          placeItems: 'start center',
        }}
      >
        <Grid
          columns="2fr 1fr 2fr"
          gap={0}
          m={[3, null, 4]}
          sx={{
            '::before': { content: '""' },
            '::after': { content: '""' },
          }}
        >
          <animated.div style={opacityTransitions[1]}>
            <Icons.RockefellercenterLogo
              style={{
                color: themeUI.theme.colors.logo,
                maxWidth: 238,
                minWidth: 150,
                width: '100%',
                height: 'auto',
              }}
            />
          </animated.div>
        </Grid>
      </Grid>
      <SearchForm suggestions={suggestions} to={to} />
      <animated.span
        style={{
          ...opacityTransitions[0],
          position: 'absolute',
          top: isMobile ? themeUI.theme.space[4] : themeUI.theme.space[5],
          right: isMobile ? themeUI.theme.space[3] : themeUI.theme.space[4],
        }}
      >
        <Button
          aria-label="Close search menu"
          onClick={onClose}
          variant="iconUnderline"
        >
          <Icons.CloseX aria-hidden />
        </Button>
      </animated.span>
    </Box>
  );
}
